import React, { use, useEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import NavBar from '../components/navbar';
import Layout from '../components/layout';
import Container from '../components/container';
import PeriodStandingsTable from '../components/period-standings-table';
import ContentCarousel from '../components/carousel';
import config from '../lib/config';

import { GetStaticProps } from 'next';
import { fetchAPI, fetchData, getPrimaryMenuItems } from '../lib/api';
import { IMenuItem } from '../models/menu-item';
import { IPage } from '../models/page';
import { ISponsor } from '../models/sponsor';
import { H2 } from '../components/text';
import { IPeriodStanding } from '../models/period-standing';

const PeriodTitles = ['first_period', 'second_period', 'third_period', 'fourth_period'] as const;

type PeriodTitle = (typeof PeriodTitles)[number];

interface IProps {
	primaryMenuItems: IMenuItem[];
	pageData: IPage;
	sponsors: ISponsor[];
	rankingsData: Record<PeriodTitle, IPeriodStanding[]>;
	calendars: Option[];
}

type Option = {
	name: string;
	id: string;
};

export default function PeriodeKlassementPage({
	primaryMenuItems,
	pageData: { page },
	sponsors,
	rankingsData,
	calendars,
}: IProps) {
	const [standingsData, setStandingsData] = useState<Record<PeriodTitle, IPeriodStanding[]>>(rankingsData);
	const [currentYear, setCurrentYear] = useState<Option | null>(calendars[0]);

	useEffect(() => {
		const fetchRoundData = async () => {
			try {
				// if the current year is the first year, use the initial data
				if (currentYear?.id === calendars[0]?.id) {
					setStandingsData(rankingsData);
				} else {
					const response = await fetch(
						`${config.apiUrl}/wp-json/statsperform/v1/season-period-standings?tmcl=${currentYear?.id}`,
					);
					const data = await response.json();
					setStandingsData(data);
				}
			} catch (error) {
				// Handle errors
				console.log(error, 'error');
			}
		};

		fetchRoundData();
	}, [currentYear, calendars, currentYear?.id, rankingsData]);

	const carouselItems = useMemo(
		() => PeriodTitles.map((period) => <PeriodStandingsTable period={standingsData[period]} key={period} />),
		[standingsData],
	);

	const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setCurrentYear({
			id: event.target.value,
			name: event.target.selectedOptions[0].text,
		});
	};

	const pageTitle = `${page.title} | ${config.siteName}`;
	const meta = page.seo?.description || page.title;

	return (
		<Layout preview sponsors={sponsors} metaTitle={pageTitle} metaDesc={meta}>
			<Head>
				<title>{pageTitle}</title>
			</Head>
			<NavBar items={primaryMenuItems} />
			<Container className="py-12 xl:py-16">
				<article>
					<H2 className="text-center py-6">{page.title}</H2>
					<div className="flex flex-col items-end mb-8">
						<label htmlFor="location" className="block text-base">
							Seizoen
						</label>
						<select
							id="location"
							value={currentYear?.id}
							onChange={handleChange}
							className="mt-2 block  rounded-md border-0 py-1.5 pl-3 pr-10  ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600"
						>
							{calendars.map((option) => (
								<option key={option.id} value={option.id}>
									{option.name}
								</option>
							))}
						</select>
					</div>

					<ContentCarousel items={carouselItems} startIndex={1} />
				</article>
			</Container>
		</Layout>
	);
}

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
	const primaryMenuItems = await getPrimaryMenuItems();
	const pageData = await fetchAPI<{ sponsors: { nodes: any[] } }>(`
		query {
			page(id: 77005, idType: DATABASE_ID) {
				title
				content
			}
			sponsors(first: 20) {
				nodes {
					title
					sponsor_fields {
						subtitle
						websiteUrl
						mainSponsor
						invertedImage {
							sourceUrl
						}
					}
				}
			}
		}
	`);

	let rankingsData: IPeriodStanding[] | null = null;

	try {
		const endpoint = `${config.apiUrl}/wp-json/statsperform/v1/period-standings`;
		const res = await fetch(endpoint);
		rankingsData = await res.json();
	} catch (error) {
		console.error('Error fetching data:', error);
	}

	let calendars: Option[] | null = null;
	const fetchCalenders = async () => {
		const endpoint = `${config.apiUrl}/wp-json/statsperform/v1/available-calendars`;
		return fetchData(endpoint);
	};

	try {
		calendars = await fetchCalenders();
	} catch (error) {
		console.error('Error fetching data:', error);
	}

	return {
		props: {
			sponsors: pageData.sponsors.nodes,
			pageData,
			preview,
			primaryMenuItems,
			rankingsData,
			calendars,
		},
		revalidate: 300,
	};
};
